(function () {
  // Drives sections/lookbook-carousel.liquid: cross-fades the slides, moves
  // the index segment, swaps the next-slide peek, and advances on a timer
  // that yields to the visitor.

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initCarousel(root) {
    if (root.__lookbookInit) return;
    root.__lookbookInit = true;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-lookbook-slide]'));
    if (slides.length < 2) return;

    var numbers = Array.prototype.slice.call(root.querySelectorAll('[data-lookbook-number]'));
    var peekFrames = Array.prototype.slice.call(root.querySelectorAll('[data-peek-frame]'));
    var peek = root.querySelector('[data-lookbook-peek]');
    var progress = root.querySelector('.lookbook__index-progress');

    var current = 0;
    var timer = null;
    var cleanup = null;
    // Matches the wipe duration in section-lookbook-carousel.css.
    var TRANSITION_MS = 900;
    var interval = parseInt(root.dataset.autoplay, 10);
    // Autoplay is opt-in per section, and never runs for visitors who have
    // asked for reduced motion — an unattended slideshow is exactly the kind
    // of movement that setting is about.
    var autoplays = !!interval && !REDUCED_MOTION.matches;

    function show(index, direction) {
      var next = (index + slides.length) % slides.length;
      if (next === current) return;

      // Which way the wipe travels. Callers that know the intent pass it;
      // jumping straight to a number infers it from the index.
      root.dataset.direction = direction || (next > current ? 'next' : 'prev');

      // Clear any half-finished transition first: clicking through quickly
      // would otherwise leave an older slide stuck holding .is-leaving.
      window.clearTimeout(cleanup);
      slides.forEach(function (slide) {
        slide.classList.remove('is-entering', 'is-leaving');
      });

      var previous = slides[current];
      previous.classList.remove('is-active');
      previous.classList.add('is-leaving');
      previous.setAttribute('aria-hidden', 'true');

      slides[next].classList.add('is-active', 'is-entering');
      slides[next].removeAttribute('aria-hidden');

      cleanup = window.setTimeout(function () {
        previous.classList.remove('is-leaving');
        slides[next].classList.remove('is-entering');
      }, TRANSITION_MS);

      if (numbers.length) {
        numbers[current].classList.remove('is-active');
        numbers[next].classList.add('is-active');
      }

      // The peek always shows the slide that comes after the one on screen,
      // so clicking it goes where it looks like it goes.
      var upcoming = (next + 1) % slides.length;
      peekFrames.forEach(function (frame) {
        frame.classList.toggle('is-active', parseInt(frame.dataset.index, 10) === upcoming);
      });

      if (progress) progress.style.setProperty('--lookbook-active', String(next));

      current = next;
    }

    // Autoplay state.
    //
    // Four separate things can pause the carousel, and previously each one
    // called stop() or start() directly. That is a race: two reasons to be
    // paused, one of them clears, and it starts again while the other still
    // applies — or a pause never lifts because the event that would have
    // lifted it (mouseleave) does not fire when the pointer leaves by
    // scrolling or by the window losing focus. Which is why it advanced
    // sometimes and not others.
    //
    // So the reasons are held as state and the timer is derived from them:
    // it runs only when every reason is clear.
    var paused = {
      offscreen: 'IntersectionObserver' in window, // until the observer says otherwise
      pointer: false,
      focus: false,
      hidden: document.hidden,
    };

    function shouldRun() {
      return autoplays && !paused.offscreen && !paused.pointer && !paused.focus && !paused.hidden;
    }

    // A self-scheduling timeout rather than setInterval: a background tab
    // throttles intervals and can release a backlog of ticks at once, which
    // would fire several transitions on top of each other on return.
    function schedule() {
      window.clearTimeout(timer);
      timer = null;
      if (!shouldRun()) return;
      timer = window.setTimeout(function () {
        timer = null;
        if (!shouldRun()) return;
        show(current + 1, 'next');
        schedule();
      }, interval);
    }

    function setPaused(reason, value) {
      if (paused[reason] === value) return;
      paused[reason] = value;
      schedule();
    }

    // Any deliberate move restarts the clock rather than leaving a stale
    // tick about to fire a moment later.
    function goTo(index, direction) {
      show(index, direction);
      schedule();
    }

    numbers.forEach(function (button) {
      button.addEventListener('click', function () {
        goTo(parseInt(button.dataset.index, 10));
      });
    });

    if (peek) {
      peek.addEventListener('click', function () {
        goTo(current + 1, 'next');
      });
    }

    // Pointer pause is limited to the controls and links. Hovering anywhere
    // in a section this large is normal while reading the page, and pausing
    // on that made the carousel look broken; hovering something clickable
    // is a real signal that the visitor is about to act on it.
    root.querySelectorAll('.lookbook__index, .lookbook__peek, .lookbook__cta, .lookbook__product').forEach(
      function (control) {
        control.addEventListener('mouseenter', function () {
          setPaused('pointer', true);
        });
        control.addEventListener('mouseleave', function () {
          setPaused('pointer', false);
        });
      }
    );

    root.addEventListener('focusin', function () {
      setPaused('focus', true);
    });

    // focusout fires when moving between children too, so confirm focus has
    // actually left the carousel before resuming.
    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) setPaused('focus', false);
    });

    document.addEventListener('visibilitychange', function () {
      setPaused('hidden', document.hidden);
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        goTo(current + 1, 'next');
      } else if (event.key === 'ArrowLeft') {
        goTo(current - 1, 'prev');
      }
    });

    // Swipe / drag.
    //
    // Pointer events rather than touch events, so the same code covers a
    // finger, a mouse drag and a pen — the carousel is draggable on the
    // desktop layout too, not only where the peek button is hidden.
    var SWIPE_MIN = 45;
    var dragOrigin = null;
    var dragMoved = false;

    root.addEventListener(
      'pointerdown',
      function (event) {
        if (event.button) return; // primary button / touch only
        dragOrigin = { x: event.clientX, y: event.clientY };
        dragMoved = false;
      },
      { passive: true }
    );

    root.addEventListener(
      'pointermove',
      function (event) {
        if (!dragOrigin) return;
        if (Math.abs(event.clientX - dragOrigin.x) > 10) dragMoved = true;
      },
      { passive: true }
    );

    root.addEventListener(
      'pointerup',
      function (event) {
        if (!dragOrigin) return;
        var dx = event.clientX - dragOrigin.x;
        var dy = event.clientY - dragOrigin.y;
        dragOrigin = null;

        if (Math.abs(dx) < SWIPE_MIN) return;
        // Mostly-horizontal only. Without this a vertical scroll that drifts
        // sideways — which is most of them on a phone — changes the slide.
        if (Math.abs(dx) < Math.abs(dy) * 1.2) return;

        goTo(dx < 0 ? current + 1 : current - 1, dx < 0 ? 'next' : 'prev');
      },
      { passive: true }
    );

    root.addEventListener(
      'pointercancel',
      function () {
        dragOrigin = null;
      },
      { passive: true }
    );

    // A drag that happens to finish on top of a link would otherwise follow
    // it. Captured so it is suppressed before the anchor sees the click.
    root.addEventListener(
      'click',
      function (event) {
        if (!dragMoved) return;
        dragMoved = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Off screen it should not be burning timers or decoding images.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            setPaused('offscreen', !entry.isIntersecting);
          });
        },
        { threshold: 0.15 }
      ).observe(root);
    } else {
      schedule();
    }
  }

  function init() {
    document.querySelectorAll('[data-lookbook]').forEach(initCarousel);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-lookbook]');
    if (root) initCarousel(root);
  });
})();
